/* eslint-disable jsx-a11y/alt-text */
import React, { useState } from "react";
import "dayjs/locale/en-gb";
import { toast } from "react-toastify";
import CoeAutocomplete from "../../CoeAutocomplete";
import { LoadingButton } from "@mui/lab";
import SearchIcon from "@mui/icons-material/Search";
import { ModeloGetUrl, ModeloPost } from "../../ModeloCrud";
import CustomDataGrid from "../../CustomDataGrid";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuditoria } from "../contexts/AuditoriaContext";
import AddIcon from "@mui/icons-material/Add";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Grid,
  Tooltip,
  IconButton,
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormControl,
} from "@mui/material";
import DeleteModel from "../../DeleteModel";

export default function AjustesView({ listaRow, afterSave }) {
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [ajustesRows, setAjustesRows] = useState([]);
  const { auditoriaActivaId } = useAuditoria();
  const [deleteAjusteId, setDeleteAjusteId] = useState(null);

  const viewportHeight = window.innerHeight;
  const gridHeight = viewportHeight - 400;

  const validationSchema = yup.object({
    clinica: yup.object().required("Seleccione una clínica"),
    odontologo: yup.object().required("Seleccione un odontólogo"),
    detalle: yup.string().required("Ingrese un detalle"),
    tipo: yup.string().oneOf(["positivo", "negativo"]).required(),
    importe: yup
      .number()
      .typeError("Ingrese un número")
      .positive("Debe ser positivo")
      .required("Ingrese un importe"),
  });

  const agregarAjuste = async (values) => {
    try {
      console.log("Valores a agregar:", values);
      const body = {
        centro_id: values.clinica_id,
        auditoria_id: auditoriaActivaId,
        odontologo_id: values.odontologo_id,
        tipo_ajuste: values.tipo === "positivo" ? 1 : -1,
        detalle: values.detalle,
        importe: values.importe,
      };
      const result = await ModeloPost("liquidacion-ajuste", null, null, body);
      if (result.error) {
        toast.error("Error al agregar ajuste: " + result.mensaje);
      } else {
        toast.success("Ajuste agregado exitosamente.");
        handleBuscarAjustes();
      }
    } catch (error) {
      toast.error("Error al agregar ajuste: " + error.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      clinica: null,
      odontologo: null,
      detalle: "",
      tipo: "positivo",
      importe: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setAdding(true);
      await agregarAjuste({
        clinica_id: values.clinica.id,
        odontologo_id: values.odontologo.id,
        detalle: values.detalle,
        tipo: values.tipo,
        importe: parseFloat(values.importe),
      });
      setAdding(false);
      resetForm({
        values: {
          clinica: values.clinica,
          odontologo: values.odontologo,
          detalle: "",
          tipo: "positivo",
          importe: "",
        },
      });
    },
  });

  const handleOnChange = () => {};
  const handleQuitar = (row) => {
    setDeleteAjusteId(row.id);
  };

  const columnsObject = [
    { field: "id", headerName: "Id", width: 30 },
    { field: "clinica", headerName: "Clínica", width: 80 },
    {
      field: "odontologo",
      headerName: "Odontólogo",
      width: 280,
      wrapLength: 50,
      textTransform: "capitalize",
    },
    { field: "tipo", headerName: "Tipo", width: 150 },
    { field: "detalle", headerName: "Detalle", width: 400, wrapLength: 250 },
    { field: "importe", headerName: "Importe", width: 120 },
    {
      field: "acciones",
      headerName: "Acciones",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Quitar ajuste">
          <IconButton onClick={() => handleQuitar(params.row)}>
            <DeleteIcon fontSize="small" color="error" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const handleBuscarAjustes = async () => {
    try {
      if (!formik.values.clinica) {
        toast.warning("Por favor, seleccione una clínica.");
        return;
      }
      formik.setFieldError("odontologo", "");

      const params = new URLSearchParams({
        centro_id: formik.values.clinica?.id,
        auditoria_id: auditoriaActivaId,
      });
      setSearching(true);
      const result = await ModeloGetUrl(
        "liquidacion-ajuste",
        null,
        "vista-ajustes",
        params
      );
      setSearching(false);
      if (result.error) {
        toast.error("Error al buscar ajustes: " + result.mensaje);
      } else if (result.listado?.length) {
        console.log("Ajustes encontrados:", result.listado);
        setAjustesRows(result.listado);
      } else {
        setAjustesRows([]);
      }
    } catch (error) {
      toast.error("Error al buscar ajustes: " + error.message);
      setSearching(false);
    }
  };

  const grabar = async (values) => {
    if (!ajustesRows?.length) {
      toast.warning("Debe haber al menos una fila.");
      return;
    }

    setSearching(true);
    const coeId = ajustesRows[0].coe_id;
    const data = {
      "lista-id": listaRow?.id,
    };

    const result = await ModeloPost(
      "centro-odontologo-especialidad",
      coeId,
      "update-lista-honorarios",
      data
    );

    if (result.error) {
      toast.error("Error al actualizar: " + result.mensaje);
    } else {
      toast.success("Actualización completada.");
      handleBuscarAjustes();
      if (afterSave) afterSave();
    }

    setSearching(false);
  };

  const handleCancelar = () => {
    formik.resetForm();
    setSearching(false);
    setAdding(false);
    setAjustesRows([]);
  };

  return (
    <Grid container spacing={2}>
      {/* Formulario en una línea */}
      <Grid item xs={12}>
        <form onSubmit={formik.handleSubmit}>
          <Grid item xs={12}>
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={2}
              flexWrap="wrap"
            >
              <Box flexGrow={1} minWidth={{ xs: "80%", sm: "300px" }}>
                <CoeAutocomplete
                  selectedClinica={formik.values.clinica}
                  selectedEspecialidad={null}
                  selectedOdontologo={formik.values.odontologo}
                  setSelectedClinica={(val) =>
                    formik.setFieldValue("clinica", val, true)
                  }
                  setSelectedEspecialidad={(val) =>
                    formik.setFieldValue("especialidad", val, true)
                  }
                  setSelectedOdontologo={(val) =>
                    formik.setFieldValue("odontologo", val, true)
                  }
                  visibleEspecialidad={false}
                  handlebusqueda={null}
                  // loading={loading}
                  handleOnChange={handleOnChange}
                  clinicaWidth="100%"
                  odontologoWidth="100%"
                  formik={formik}
                />
              </Box>

              <Tooltip title="Buscar ajustes del período">
                <LoadingButton
                  size="medium"
                  onClick={handleBuscarAjustes}
                  loading={searching}
                  loadingPosition="start"
                  endIcon={<SearchIcon />}
                  variant="contained"
                  sx={{
                    backgroundColor: "primary.main",
                    ":hover": { bgcolor: "#475569", color: "white" },
                    width: 80,
                    minWidth: 80,
                    alignSelf: { xs: "flex-start", sm: "center" },
                    mt: -3,
                  }}
                >
                  <span></span>
                </LoadingButton>
              </Tooltip>
            </Box>
          </Grid>

          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems="center"
            gap={3}
            flexWrap="wrap"
            sx={{ ml: 1, mt: { xs: 0, sm: -2 } }}
          >
            <TextField
              label="Detalle"
              name="detalle"
              size="small"
              value={formik.values.detalle}
              onChange={formik.handleChange}
              error={formik.touched.detalle && Boolean(formik.errors.detalle)}
              helperText={formik.touched.detalle && formik.errors.detalle}
              sx={{
                minWidth: {
                  xs: "100%", // en pantallas pequeñas ocupa todo
                  sm: 300, // a partir de sm (600px) mínimo 300px
                  md: 400, // a partir de md (900px) mínimo 400px
                  lg: 600, // a partir de lg (1200px) mínimo 600px
                },
              }}
            />

            <FormControl size="small">
              <FormLabel>Tipo</FormLabel>
              <RadioGroup
                row
                name="tipo"
                value={formik.values.tipo}
                onChange={formik.handleChange}
              >
                <FormControlLabel
                  value="positivo"
                  control={<Radio size="small" />}
                  label="Positivo"
                />
                <FormControlLabel
                  value="negativo"
                  control={<Radio size="small" />}
                  label="Negativo"
                />
              </RadioGroup>
            </FormControl>

            <TextField
              label="Importe"
              name="importe"
              size="small"
              type="number"
              value={formik.values.importe}
              onChange={formik.handleChange}
              error={formik.touched.importe && Boolean(formik.errors.importe)}
              helperText={formik.touched.importe && formik.errors.importe}
              sx={{
                minWidth: {
                  xs: "100%", // en pantallas pequeñas ocupa todo
                  sm: 150, // a partir de sm (600px) mínimo 150px
                },
              }}
            />

            <Tooltip title="Agregar ajuste">
              <LoadingButton
                size="medium"
                type="submit"
                loading={adding}
                loadingPosition="start"
                endIcon={<AddIcon />}
                variant="contained"
                sx={{
                  backgroundColor: "primary.main",
                  ":hover": { bgcolor: "#475569", color: "white" },
                  width: 80,
                  minWidth: 80,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  ml: 3,
                }}
              >
                <span></span>
              </LoadingButton>
            </Tooltip>
          </Box>
        </form>
      </Grid>
      {/* Tabla de ajustes */}
      <Grid item xs={12}>
        {!searching && !adding && ajustesRows?.length > 0 && (
          <CustomDataGrid
            columnsObject={columnsObject}
            dataRows={ajustesRows}
            pageSize={100}
            height={gridHeight}
            columnPercentage={0.4}
          />
        )}
      </Grid>
      {deleteAjusteId !== null && (
        <DeleteModel
          model={"liquidacion-ajuste"}
          modelid={deleteAjusteId}
          okmethod={handleBuscarAjustes}
          nullmethod={setDeleteAjusteId}
        />
      )}
    </Grid>
  );
}
